import React, { useState, useEffect } from 'react';
import {
  Container,
  Form,
  Button,
  Image,
  Card,
  Row,
  Col,
  Badge,
} from 'react-bootstrap';
import { Organization, SUPPORTED_CURRENCIES } from '../../models';
import { useLoading } from '../../hooks/useLoading';
import Logo from '../../res/MayorX.svg';
import { updateOrganization } from '../../api/organizations';

interface CompleteProfileProps {
  organization: Organization;
  onComplete: () => void;
  addToast: (message: string) => void;
}

const CompleteProfile: React.FC<CompleteProfileProps> = ({
  organization,
  onComplete,
  addToast,
}) => {
  const { showLoading, hideLoading } = useLoading();

  const [organizationName, setOrganizationName] = useState({
    value: organization.name.startsWith('Organización de ')
      ? ''
      : organization.name,
    errorMessage: '',
    valid:
      !organization.name.startsWith('Organización de ') &&
      organization.name.length >= 2,
  });

  const initialPhone = organization.phone ?? '';
  const [phone, setPhone] = useState({
    value: initialPhone,
    errorMessage: '',
    valid: initialPhone.trim().length >= 8,
  });

  const [selectedCurrency, setSelectedCurrency] = useState('VES');
  const [useReferenceCurrency, setUseReferenceCurrency] = useState(true);
  const [selectedReferenceCurrency, setSelectedReferenceCurrency] =
    useState('USD');
  const [exchangeRate, setExchangeRate] = useState('36.0');

  const handleChange = (prop: string, value: string) => {
    if (prop === 'organizationName') {
      const isValid = value.length >= 2;
      const errorMessage = isValid
        ? ''
        : 'Nombre de la organización debe tener al menos 2 caracteres';
      setOrganizationName({ value, valid: isValid, errorMessage });
    }
    if (prop === 'phone') {
      const trimmed = value.trim();
      // Allow digits, spaces, +, -, (), and common separators
      const isValid = trimmed.length >= 8;
      const errorMessage = isValid
        ? ''
        : 'Ingresa un número de teléfono válido (mín. 8 caracteres)';
      setPhone({ value: value, valid: isValid, errorMessage });
    }
  };

  const isFormValid = () => {
    return organizationName.valid && phone.valid;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    showLoading('Completando perfil...');

    try {
      // In a full implementation, we'd also update the currency/factors here.
      // Since those are in a subcollection, it's typically handled by a separate API call,
      // but for now we focus on the organization basic info and the flag.
      await updateOrganization(organization.id, {
        name: organizationName.value,
        phone: phone.value.trim() || undefined,
        isProfileComplete: true,
      });

      hideLoading();
      addToast('Perfil completado exitosamente');
      onComplete();
    } catch (error) {
      console.error(error);
      hideLoading();
      addToast('Error al actualizar el perfil');
    }
  };

  return (
    <Container
      style={{ maxWidth: '960px', paddingTop: '2rem', paddingBottom: '2rem' }}
    >
      <div className="d-flex flex-column align-items-center gap-4">
        <div className="text-center">
          <Image
            src={Logo}
            alt="Wholesale System Logo"
            className="official-logo mb-4"
            style={{ maxWidth: '150px', width: 'min(150px, 70vw)' }}
          />
          <h2 className="h4 fw-bold">Completa tu Organización</h2>
          <p className="text-muted small">
            Faltan algunos detalles para terminar de configurar tu cuenta.
          </p>
        </div>

        <div className="w-100 bg-body p-4 rounded shadow-sm border">
          <div className="d-flex flex-column gap-4">
            <div className="w-100">
              <h3 className="h6 mb-3 text-secondary">
                Información de la Organización
              </h3>
              <div className="d-flex flex-column gap-2">
                <Form.Label className="fw-medium">
                  Nombre de la Organización *
                </Form.Label>
                <Form.Control
                  type="text"
                  value={organizationName.value}
                  onChange={e =>
                    handleChange('organizationName', e.currentTarget.value)
                  }
                  placeholder="Ej: Mi Empresa S.A."
                  isInvalid={!!organizationName.errorMessage}
                />
                <Form.Control.Feedback type="invalid">
                  {organizationName.errorMessage}
                </Form.Control.Feedback>
              </div>
              <div className="d-flex flex-column gap-2 mt-3">
                <Form.Label className="fw-medium">Teléfono *</Form.Label>
                <Form.Control
                  type="tel"
                  value={phone.value}
                  onChange={e => handleChange('phone', e.currentTarget.value)}
                  placeholder="Ej: +58 412 1234567"
                  isInvalid={!!phone.errorMessage}
                />
                <Form.Control.Feedback type="invalid">
                  {phone.errorMessage}
                </Form.Control.Feedback>
              </div>
            </div>

            <div className="w-100">
              <h3 className="h6 mb-3 text-secondary">Configuración Regional</h3>
              <Form.Label className="fw-medium mb-3">
                Moneda Principal (Predeterminada)
              </Form.Label>
              <Row className="g-3">
                {SUPPORTED_CURRENCIES.map(currency => (
                  <Col key={currency.code} xs={12} sm={6} md={4}>
                    <Card
                      className={`h-100 ${
                        currency.code === selectedCurrency
                          ? 'border-primary bg-primary bg-opacity-20'
                          : 'border bg-body-tertiary'
                      }`}
                      style={{
                        cursor:
                          currency.code === selectedCurrency
                            ? 'default'
                            : 'not-allowed',
                        opacity: currency.code === selectedCurrency ? 1 : 0.6,
                        borderWidth:
                          currency.code === selectedCurrency ? '2px' : '1px',
                      }}
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className={`fw-bold fs-5 ${currency.code === selectedCurrency ? 'text-white' : 'text-muted'}`}
                          >
                            {currency.symbol}
                          </div>
                          <div>
                            <div
                              className={`fw-medium ${currency.code === selectedCurrency ? 'text-white' : 'text-muted'}`}
                            >
                              {currency.code}
                            </div>
                            <div
                              className={`small ${currency.code === selectedCurrency ? 'text-white' : 'text-muted'}`}
                            >
                              {currency.name}
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>

            <Button
              variant="primary"
              size="lg"
              className="w-100 mt-2"
              onClick={handleSubmit}
              disabled={!isFormValid()}
            >
              FINALIZAR CONFIGURACIÓN
            </Button>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default CompleteProfile;
